// BuyChatCreditsModal - Buy chat credit packs via Razorpay
// 25 credits = ₹500, multiples up to 500 credits

import React, { useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Box from '@mui/joy/Box';
import Alert from '@mui/joy/Alert';
import CircularProgress from '@mui/joy/CircularProgress';
import { supabase } from '../utils/supabaseClient';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  userEmail: string;
  currentCredits: number;
}

const PACKS = [
  { credits: 25, price: 500 },
  { credits: 50, price: 1000 },
  { credits: 75, price: 1500 },
  { credits: 100, price: 2000 },
];

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function BuyChatCreditsModal({ open, onClose, onSuccess, userId, userEmail, currentCredits }: Props) {
  const [selected, setSelected] = useState<number>(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPack = PACKS.find(p => p.credits === selected)!;

  const handlePay = async () => {
    setError(null);
    setLoading(true);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load payment gateway. Please try again.');

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Session expired. Please refresh and try again.');

      // Create Razorpay order
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const orderRes = await fetch(`${supabaseUrl}/functions/v1/create-credits-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ credits_count: selected }),
      });

      const orderData = await orderRes.json();
      if (!orderRes.ok || !orderData.order) throw new Error(orderData.error || 'Failed to create order');

      const { order, purchase_id } = orderData;

      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: order.amount,
          currency: order.currency,
          name: 'ShreemAstro',
          description: `${selected} AI Chat Credits`,
          order_id: order.id,
          prefill: { email: userEmail },
          theme: { color: '#4A9EFF' },
          handler: async (response: any) => {
            try {
              const verifyRes = await fetch(`${supabaseUrl}/functions/v1/verify-credits-payment`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  purchase_id,
                }),
              });

              const verifyData = await verifyRes.json();
              if (!verifyRes.ok || !verifyData.success) {
                reject(new Error(verifyData.error || 'Payment verification failed'));
                return;
              }
              resolve();
            } catch (e: any) {
              reject(e);
            }
          },
          modal: {
            ondismiss: () => reject(new Error('cancelled')),
          },
        });
        rzp.open();
      });

      onSuccess();
      onClose();
    } catch (e: any) {
      if (e.message !== 'cancelled') {
        setError(e.message || 'Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 11000 }}>
      <ModalDialog
        sx={{
          maxWidth: 440,
          width: '95vw',
          maxHeight: '92vh',
          borderRadius: '16px',
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 11000,
          boxShadow: '0 20px 60px rgba(184,48,80,0.25), 0 4px 20px rgba(0,0,0,0.12)',
          border: '1px solid rgba(184,48,80,0.15)',
        }}
      >
        {/* ── Gradient Header ── */}
        <Box sx={{
          background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 50%, #4a0a18 100%)',
          px: 3, py: 2.75,
          position: 'relative',
        }}>
          <ModalClose sx={{
            color: 'rgba(255,255,255,0.65)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            top: 12, right: 12,
          }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.1rem',
            }}>✦</Box>
            <Typography level="h4" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.01em' }}>
              AI Chat Credits
            </Typography>
          </Box>
          <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.72)', pl: 0.25 }}>
            Unlock deeper questions about every life area
          </Typography>
          {currentCredits > 0 && (
            <Box sx={{
              mt: 1.5, display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1.25, py: 0.4, borderRadius: '999px',
              bgcolor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}>
              <Typography sx={{ color: '#fff', fontSize: '0.73rem', fontWeight: 600 }}>
                ✦ Current balance: {currentCredits} credits
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Body (scrollable) ── */}
        <Box sx={{ p: 3, pb: 2, bgcolor: '#fff', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <Typography sx={{
            mb: 1.75, fontSize: '0.66rem', fontWeight: 700,
            color: '#7a1a2e', textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Select a pack
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            {PACKS.map(pack => (
              <Box
                key={pack.credits}
                onClick={() => setSelected(pack.credits)}
                sx={{
                  border: '2px solid',
                  borderColor: selected === pack.credits ? '#b83050' : 'rgba(184,48,80,0.18)',
                  borderRadius: '10px',
                  p: 1.25,
                  cursor: 'pointer',
                  background: selected === pack.credits
                    ? 'linear-gradient(135deg, rgba(184,48,80,0.07) 0%, rgba(122,26,46,0.04) 100%)'
                    : 'transparent',
                  transition: 'all 0.15s ease',
                  textAlign: 'center',
                  position: 'relative',
                  '&:hover': {
                    borderColor: 'rgba(184,48,80,0.5)',
                    background: 'rgba(184,48,80,0.04)',
                  },
                }}
              >
                {selected === pack.credits && (
                  <Box sx={{
                    position: 'absolute', top: -8, right: 8,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#b83050',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.55rem', fontWeight: 700 }}>✓</Typography>
                  </Box>
                )}
                <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#3d0d1a', lineHeight: 1.1 }}>
                  {pack.credits}
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#7a1a2e', opacity: 0.65, mb: 0.75, fontWeight: 500 }}>
                  credits
                </Typography>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  px: 1, py: 0.25, borderRadius: '999px',
                  background: selected === pack.credits
                    ? 'linear-gradient(135deg, #c8960c 0%, #a07008 100%)'
                    : 'rgba(200,150,12,0.12)',
                  border: '1px solid',
                  borderColor: selected === pack.credits ? 'transparent' : 'rgba(200,150,12,0.35)',
                }}>
                  <Typography sx={{
                    fontSize: '0.75rem', fontWeight: 700,
                    color: selected === pack.credits ? '#fff' : '#7a5a00',
                  }}>
                    ₹{pack.price.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Cost per credit */}
          <Box sx={{
            px: 1.5, py: 1,
            borderRadius: '10px',
            bgcolor: 'rgba(184,48,80,0.04)',
            border: '1px solid rgba(184,48,80,0.1)',
          }}>
            <Typography sx={{ textAlign: 'center', fontSize: '0.72rem', color: '#7a1a2e', fontWeight: 500 }}>
              ₹{(selectedPack.price / selectedPack.credits).toFixed(0)} per credit · Credits never expire · No subscription required
            </Typography>
          </Box>
        </Box>

        {/* ── Sticky Footer ── */}
        <Box sx={{ px: 3, pb: 2.5, pt: 1.5, bgcolor: '#fff', borderTop: '1px solid rgba(184,48,80,0.08)', flexShrink: 0 }}>
          {error && (
            <Alert color="danger" size="sm" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            size="lg"
            onClick={handlePay}
            loading={loading}
            disabled={loading}
            sx={{
              fontWeight: 700,
              fontSize: '0.95rem',
              background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              py: 1.5,
              boxShadow: '0 4px 15px rgba(184,48,80,0.35)',
              '&:hover:not(:disabled)': {
                background: 'linear-gradient(135deg, #c9385a 0%, #8b2030 100%)',
                boxShadow: '0 6px 20px rgba(184,48,80,0.45)',
              },
              '&:disabled': { opacity: 0.65 },
            }}
          >
            {loading ? <CircularProgress size="sm" sx={{ '--CircularProgress-progressColor': '#fff' }} /> : `Pay ₹${selectedPack.price.toLocaleString('en-IN')} for ${selectedPack.credits} Credits →`}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.tertiary' }}>
              🔒 Secured by Razorpay · No auto-renewal
            </Typography>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
